import { NavLink } from 'react-router';
import { ChevronDown } from 'lucide-react';
import clsx from 'clsx';
import type { ReactNode } from 'react';

interface SidebarItemProps {
  to: string;
  icon: React.ElementType;
  label: string;
  isCollapsed: boolean;
  end?: boolean;
  onClick?: () => void;
}

export const SidebarItem = ({ to, icon: Icon, label, isCollapsed, end, onClick }: SidebarItemProps) => {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      className={({ isActive }) => clsx(
        'group relative flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors font-medium',
        isActive
          ? 'bg-primary text-white'
          : 'text-charcoal hover:bg-gray-100'
      )}
    >
      <Icon size={24} className="shrink-0" />

      {!isCollapsed ? (
        <span className="whitespace-nowrap overflow-hidden transition-all duration-300">
          {label}
        </span>
      ) : (
        // Tooltip for collapsed mode
        <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 bg-gray-900 text-white text-xs px-2 py-1.5 rounded-sm shadow-lg whitespace-nowrap z-50 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity">
          {label}
          <div className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-gray-900 rotate-45" />
        </div>
      )}
    </NavLink>
  );
};

interface SidebarGroupProps {
  id: string;
  icon: React.ElementType;
  label: string;
  isOpen: boolean;
  isCollapsed: boolean;
  onToggle: (id: string) => void;
  children: ReactNode;
}

export const SidebarGroup = ({ id, icon: Icon, label, isOpen, isCollapsed, onToggle, children }: SidebarGroupProps) => {
  return (
    <div className="relative group/menu">
      <button
        onClick={() => onToggle(id)}
        className={clsx(
          'w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors font-medium text-charcoal hover:bg-gray-100 group',
          isOpen && isCollapsed && 'bg-gray-100' // Highlight when flyout open
        )}
      >
        <div className="flex items-center gap-3">
          <Icon size={24} className={clsx('shrink-0', isOpen && 'text-primary')} />
          {!isCollapsed && <span>{label}</span>}
        </div>

        {!isCollapsed && (
          <ChevronDown
            size={16}
            className={clsx('transition-transform text-gray-400', isOpen && 'rotate-180')}
          />
        )}
      </button>

      {/* Group Content */}
      {isOpen && (
        <div
          className={clsx(
            isCollapsed
              ? 'absolute left-full top-0 ml-2 w-56 bg-white shadow-xl rounded-lg border border-gray-200 p-2 z-50' // Flyout
              : 'ml-4 mt-1 pl-3 border-l-2 border-gray-200 space-y-0.5' // Accordion
          )}
        >
          {isCollapsed && (
            <div className="px-3 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1">
              {label}
            </div>
          )}
          {children}
        </div>
      )}
    </div>
  );
};

export const subNavLinkClass = ({ isActive }: { isActive: boolean }) =>
  clsx(
    'flex items-center gap-3 px-3 py-2 rounded-md transition-colors text-sm',
    isActive
      ? 'bg-primary text-white'
      : 'text-gray-600 hover:bg-gray-100 hover:text-charcoal'
  );
